import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';

import {FormInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {WidthItem} from 'src/components/width-item';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {CustomValidatedControl, ValidatedControl} from 'src/contexts/validation-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEntitlementNamespaces} from 'src/hooks/dictionaries';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {parseAndPrettyPrintJson} from 'src/modules/entitlements/utils/parseAndPrettyPrintJson';
import {ResourceType} from 'src/openapi/admin/models/ResourceType';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import {Dialog} from '@pb/kit-component-dialog-v2';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import Loading from '@pb/kit-component-loading';
import {Skeleton} from '@pb/kit-component-skeleton';
import {Textarea} from '@pb/kit-component-textarea-v2';
import {useShown} from '@pb/kit-hooks-use-shown';


import {ManageEntitlementsPage} from '../components/manage-entitlements-page';
import {RESOURCE_DESCRIPTION_CONTROL, RESOURCE_METADATA_CONTROL, RESOURCE_NAME_CONTROL} from '../constants';
import {getEntitlementsReturnPath} from '../utils';
import {isValidJsonString} from '../utils/validateIsJson';

import {EntitlementResourceChangeLog} from './change-log';
import {REMOVE_RESOURCE_MODAL_WIDTH, RemoveResourceModal} from './remove-resource-modal';
import {useUpdateResource} from './useUpdateResource';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {CheckBoxChangeData} from 'src/typedef';


const UpdateResource = () => {
    const queryParams = useParams<{resourceId: string}>();
    const resourceId = queryParams.resourceId || '';
    const navigate = useNavigate();
    const {state: navigationState} = useLocation();

    const {shown: deleteShown, onClick: openDelete, onClose: closeDelete} = useShown();
    const {hasAccess} = useAdminAccessRight();

    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);
    const {editResourceOpen, editResourceSubmit} = useEventTracking();
    const {proceed, setIsChunkChanged} = useConfirmationChunk({chunkName: 'update-resource', onProceed: () => {
        setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
    }});

    const [busy, setBusy] = useState(false);
    const [metadataInput, setMetadataInput] = useState<string>('');

    const {getNamespaceByCode} = useEntitlementNamespaces();

    const {
        data,
        dataInitial,
        setData,
        isChanged,
        loadingError,
        isLoading,
        saveChanges: updateResourceAction,
        discardChanges: discardResourceChanges,
    } = useUpdateResource({resourceId});

    useEffect(() => {
        editResourceOpen();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setIsChunkChanged(isChanged);
    }, [isChanged, setIsChunkChanged]);

    useEffect(() => {
        if (data.metadata && data.metadata.trim() && isValidJsonString(data.metadata)) {
            setMetadataInput(parseAndPrettyPrintJson(data.metadata));
        } else {
            setMetadataInput(data.metadata || '');
        }
    }, [data.metadata]);

    const handleMetadataChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        setMetadataInput(value);
        setData(prev => ({...prev, metadata: value}));
    }, [setData]);

    const handleMetadataBlur = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
        const value = event.currentTarget.value.trim();
        if (value && isValidJsonString(value)) {
            const pretty = parseAndPrettyPrintJson(value);
            setMetadataInput(pretty);
            setData(prev => {
                if (parseAndPrettyPrintJson(prev.metadata ?? '') === pretty) {
                    return prev;
                }

                return {...prev, metadata: pretty};
            });
        } else {
            setMetadataInput(value);
            setData(prev => ({...prev, metadata: value}));
        }
    }, [setData]);

    const changeResourceName = useCallback((name: string) => {
        setData(prev => ({...prev, name}));
    }, [setData]);

    const changeResourceTypes = useCallback((changeData: CheckBoxChangeData) => {
        const types = changeData.event.target.checked ? [ResourceType.DATASET] : undefined;
        setData(prev => ({...prev, types}));
    }, [setData]);

    const changeDescription = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setData(prev => ({...prev, description: event.target.value}));
    }, [setData]);

    const trimDescription = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
        setData(prev => ({...prev, description: event.currentTarget.value.trim()}));
    }, [setData]);

    const backToAllEntitlements = useCallback(() => {
        navigate(-1);
        editResourceSubmit({success: true, submit: false});
    }, [editResourceSubmit, navigate]);

    const saveChanges = useCallback(async () => {
        setBusy(true);
        const updateResourceError = await updateResourceAction();
        setBusy(false);

        if (updateResourceError) {
            setErrorMessage(updateResourceError);
        } else {
            proceed();
        }

        editResourceSubmit({success: !updateResourceError, submit: true});
    }, [updateResourceAction, editResourceSubmit, proceed, setErrorMessage]);

    const discardChanges = useCallback(() => {
        editResourceSubmit({success: true, submit: false});
        discardResourceChanges();
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [discardResourceChanges, setSuccessMessage, editResourceSubmit]);

    return (
        <Skeleton shown={isLoading} view={<Loading fast={true}/>}>
            <ServerResponseHandler
                serverError={loadingError}
                accessRight={ACCESS_RIGHTS.MANAGE_ENTITLEMENTS as AccessRight}
                sectionName="Edit Resource">
                <ManageEntitlementsPage
                    title={dataInitial.name}
                    buttonsBlock={(
                        <Group>
                            {hasAccess(ACCESS_RIGHTS.DELETE_ENTITLEMENTS) ? (
                                <Button variant="Type.SECONDARY" colorVariant="ERROR" onClick={openDelete}>
                                    Delete
                                </Button>
                            ) : null}
                            <Button variant="Type.TERTIARY" onClick={backToAllEntitlements}>
                                Cancel
                            </Button>
                            <Button variant="Type.SECONDARY" disabled={!isChanged} onClick={discardChanges}>
                                Discard Changes
                            </Button>
                            <Button variant="Type.PRIMARY" busy={busy} disabled={!isChanged} onClick={saveChanges}>
                                Save Changes
                            </Button>
                        </Group>
                    )}>
                    <Item className="group-item pd-l">
                        <FormSection headline="Resource Information">
                            <Group direction="v">
                                <CustomValidatedControl name={RESOURCE_NAME_CONTROL}>
                                    {error => (
                                        <FormInput
                                            value={data.name}
                                            width={300}
                                            maxLength={64}
                                            label={<RequiredMark label="Resource Name"/>}
                                            placeholder="Resource Name"
                                            onBlur={changeResourceName}
                                            valid={error ? false : undefined}
                                            errorDescription={error}
                                        />
                                    )}
                                </CustomValidatedControl>
                                <FormInput
                                    value={data.id}
                                    width={300}
                                    readOnly={true}
                                    label={<RequiredMark label="Resource External ID"/>}
                                />
                                <ValidatedControl name={RESOURCE_DESCRIPTION_CONTROL}>
                                    {invalid => (
                                        <WidthItem width={610}>
                                            <Label><RequiredMark label="Description"/></Label>
                                            <Textarea
                                                maxLength={255}
                                                color={invalid ? 'ERROR' : 'DEFAULT'}
                                                value={data.description}
                                                onChange={changeDescription}
                                                onBlur={trimDescription}
                                            />
                                        </WidthItem>
                                    )}
                                </ValidatedControl>
                                <ValidatedControl name={RESOURCE_METADATA_CONTROL}>
                                    {invalid => (
                                        <WidthItem width={610}>
                                            <Label>Metadata</Label>
                                            <Textarea
                                                color={invalid ? 'ERROR' : 'DEFAULT'}
                                                value={metadataInput}
                                                onChange={handleMetadataChange}
                                                onBlur={handleMetadataBlur}
                                            />
                                        </WidthItem>
                                    )}
                                </ValidatedControl>
                                <FormInput
                                    value={getNamespaceByCode(data.namespace)?.description || ''}
                                    width={300}
                                    readOnly={true}
                                    label="Namespace"
                                />
                                <CheckBox
                                    label="Dataset"
                                    checked={Boolean(data.types?.includes(ResourceType.DATASET))}
                                    onCheckBoxChange={changeResourceTypes}
                                />
                            </Group>
                        </FormSection>
                    </Item>
                    <EntitlementResourceChangeLog resourceId={resourceId}/>
                    <Dialog
                        shown={deleteShown}
                        setShown={closeDelete}
                        wrapperProps={{style: {width: REMOVE_RESOURCE_MODAL_WIDTH}}}>
                        <RemoveResourceModal
                            resourceId={resourceId}
                            resourceName={dataInitial.name}
                            onClose={closeDelete}
                            onDone={() => {
                                closeDelete();
                                setSuccessMessage('Resource removed successfully');
                                navigate(getEntitlementsReturnPath(navigationState));
                            }}
                        />
                    </Dialog>
                </ManageEntitlementsPage>
            </ServerResponseHandler>
        </Skeleton>
    );
};

export const ValidateUpdateResource = () => (
    <ValidationContextProvider>
        <UpdateResource/>
    </ValidationContextProvider>
);
